import { ErrorCircle, HelpCircle } from '@ori-ui/icons';
import { Box, FormControl, FormHelperText, Grid, styled, TextField, Tooltip } from '@ori-ui/mui';
import type { GlobalSharedMetadataValidationStringValidationDto } from '@ori/global-api-client-react';
import useTestId from '@ori/testid-generator';
import camelCase from 'lodash.camelcase';
import split from 'lodash.split';
import type { FC, ReactElement } from 'react';
import { useEffect } from 'react';
import type { ControllerFieldState } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';

import { Localization } from '../../../Localization';
import { defaultTranslations } from '../../../constants/defaultTranslations_simplified-registration-BrandPartner';
import { useConfiguration, useFormError } from '../../../contexts';
import { TranslationKey } from '../../../models';
import { getErrorLocalizationByCode, renderValidationMessage } from '../../../utils';

type InputMode = 'decimal' | 'email' | 'none' | 'numeric' | 'search' | 'tel' | 'text' | 'url';
interface ITextFormFieldByConfigProps {
  name: string;
  label?: ReactElement | string;
  tooltipText?: ReactElement | string;
  isEditable?: boolean;
  isAvailable?: boolean;
  isAddressMatchRequired?: boolean;
  displayOrder?: number;
  validation?: GlobalSharedMetadataValidationStringValidationDto;
  fieldState?: ControllerFieldState;
  initialValue?: string;
  keyboardType?: InputMode;
  customPlaceholder?: string;
  inputType?: React.InputHTMLAttributes<unknown>['type'];
  enableLowercaseLetters?: boolean;
  useUpperCase?: boolean;
  allowOnlyNumbers?: boolean;
}

const StyledHelpCircle = styled(HelpCircle)(({ theme }) => ({
  fontSize: '30px',
  color: theme.palette.grey[300],
}));

const StyledLink = styled('a')(({ theme }) => ({
  color: theme.palette.error.main,
  textDecoration: 'underline',
  '&:hover': {
    color: theme.palette.error.main,
    textDecoration: 'underline',
  },
}));

const handleChangeAllowOnlyNumber = (value: string, available?: boolean) =>
  available ? value.replaceAll(/\D/g, '') : value;

export const TextFormFieldByConfig: FC<ITextFormFieldByConfigProps> = (props) => {
  const {
    name,
    label,
    tooltipText,
    isEditable,
    isAvailable,
    displayOrder,
    validation,
    initialValue,
    isAddressMatchRequired,
    keyboardType,
    inputType,
    customPlaceholder,
    enableLowercaseLetters,
    useUpperCase,
    allowOnlyNumbers,
  } = props;

  const {
    context: { errorData },
  } = useFormError();
  const { customErrors } = errorData;

  const { customerLoginUrl } = useConfiguration();
  const { setFocus, setValue } = useFormContext();

  const { getTestIdProps, getTestId } = useTestId();

  const submitError = customErrors?.find((error) => error.fieldName?.toLowerCase() === name.toLowerCase());

  const formatError = split(submitError?.fieldName, '.').map((item) => camelCase(item));
  const joinFormatError = formatError.join('.');

  useEffect(() => {
    if (submitError) {
      setFocus(joinFormatError);
    }
  }, [customErrors, joinFormatError, setFocus, submitError]);

  const errorMessage = getErrorLocalizationByCode(submitError?.errorCode, name);

  const regexPattern = validation?.regularExpression ? new RegExp(validation.regularExpression) : undefined;

  const readOnly = isAddressMatchRequired ?? false;
  const fieldPlaceholder = customPlaceholder;

  const capitalize = enableLowercaseLetters ? 'off' : 'on';
  const readOnlyStyleBox = {
    display: 'flex',
    height: '0',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const renderHelpTooltip = () => (
    <Tooltip
      title={tooltipText ?? ''}
      enterTouchDelay={0}
      leaveTouchDelay={5000}
    >
      <StyledHelpCircle />
    </Tooltip>
  );

  const handleTrimValue = (fieldValue?: string) => {
    const correctValue = useUpperCase ? fieldValue?.toLocaleUpperCase() : fieldValue;
    setValue(name, correctValue?.trim(), {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  // we need start tabindex from 1 but displayOrder starts from 0
  const correctTabIndex = typeof displayOrder === 'number' ? displayOrder + 1 : displayOrder;

  const renderField = () => (
    <Grid
      item
      xs={12}
      sm={12}
      style={{ order: displayOrder }}
    >
      <Box
        margin={readOnly ? '0' : '0 auto 25px'}
        maxWidth={500}
        style={readOnly ? readOnlyStyleBox : undefined}
      >
        <Controller
          name={name}
          rules={{
            required: validation?.isMandatory,
            maxLength: validation?.maxLength,
            minLength: validation?.minLength,
            pattern: regexPattern,
          }}
          defaultValue={initialValue}
          render={({ field: { ref, onChange, ...field }, fieldState }) => (
            <FormControl fullWidth>
              {readOnly ? null : (
                <TextField
                  fullWidth
                  type={inputType ?? 'text'}
                  label={label}
                  placeholder={fieldPlaceholder}
                  variant="outlined"
                  error={!!fieldState.error || Boolean(submitError)}
                  helperText={
                    fieldState.error ? (
                      <div {...getTestIdProps(`${name}-text-field-error-message`)}>
                        {renderValidationMessage(fieldState.error)}
                      </div>
                    ) : null
                  }
                  disabled={!isEditable}
                  required={validation?.isMandatory}
                  defaultValue={field.value as string}
                  inputProps={{
                    tabIndex: correctTabIndex,
                    maxLength: validation?.maxLength,
                    minLength: validation?.minLength ?? undefined,
                    inputMode: keyboardType ?? 'text',
                    'data-testid': getTestId(`${name}-text-field`),
                    autoCapitalize: capitalize,
                    style: { textTransform: useUpperCase ? 'uppercase' : 'initial' },
                    sx: {
                      '&::placeholder': {
                        textTransform: 'initial',
                      },
                    },
                  }}
                  // eslint-disable-next-line react/jsx-no-duplicate-props --- we needed is not duplicate props
                  InputProps={{
                    endAdornment: fieldState.error ? <ErrorCircle color="error" /> : renderHelpTooltip(),
                    autoComplete: 'off',
                  }}
                  {...field}
                  value={(field.value as string) || ''}
                  inputRef={ref}
                  onBlur={() => handleTrimValue(field.value as string)}
                  onChange={(e) => onChange(handleChangeAllowOnlyNumber(e.target.value, allowOnlyNumbers))}
                />
              )}
              {submitError ? (
                <FormHelperText error>
                  <Localization
                    {...errorMessage}
                    values={{
                      loginLink: (
                        <StyledLink
                          href={customerLoginUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Localization
                            id={TranslationKey.loginLinkLabel}
                            defaultMessage={defaultTranslations.loginLinkLabel}
                          />
                        </StyledLink>
                      ),
                    }}
                  />
                </FormHelperText>
              ) : null}
            </FormControl>
          )}
        />
      </Box>
    </Grid>
  );

  const fieldComponent = isAvailable && renderField();

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment -- we need fragment
    <>{fieldComponent}</>
  );
};
